import { Call, Device } from "@twilio/voice-sdk";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { VoiceClientEvent } from "~/modules/client/Client/ClientEvent";
import { Action } from "~/modules/actions/Action";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { getLogger } from "~/modules/logger/LoggerFactory/getLogger";
import { LoggerName } from "~/modules/logger/Logger/LoggerName";
import { VoiceController } from "~/modules/voice/VoiceController";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { VoiceCall, VoiceCallImpl } from "~/modules/VoiceCall";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface AddVoiceEventListenerOptions {
    voiceDevice?: Device;
}




export interface AddVoiceListenerEvent {
    incoming: (call: VoiceCall) => void;
    error: (error: Error, call?: VoiceCall) => void;
    destroyed: () => void;
    tokenWillExpire: () => void;
    unregistered: () => void;
}




export interface AddVoiceEventListenerResponse {
    


    unsubscribe: () => void;
}



































export class AddVoiceEventListener<T extends keyof AddVoiceListenerEvent>
    implements Action<Promise<AddVoiceEventListenerResponse>>
{
    
    
    readonly #eventName: T;

    readonly #listener: AddVoiceListenerEvent[T];

    readonly #options?: AddVoiceEventListenerOptions;

    constructor(eventName: T, listener: AddVoiceListenerEvent[T], options?: AddVoiceEventListenerOptions) {
        this.#eventName = eventName;
        this.#listener = listener;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Client);
        const voiceController: VoiceController = context.retrieveInstanceOf(
            ContextInstance.VoiceControllerImpl,
            VoiceControllerImpl
        );

        try {
            await voiceController.init(this.#options?.voiceDevice);
            const voiceDevice = voiceController.voiceDevice;

            if (!voiceDevice) {
                const errorMessage = "AddVoiceEventListener: VoiceController does not have a voiceDevice initialized";
                return Promise.reject(
                    createFlexSdkError(context)(
                        ErrorCode.VoiceDeviceNotInitialized,
                        { severity: ErrorSeverity.Error },
                        errorMessage
                    )
                );
            }

            const handler = this.#createEventHandler(context, this.#eventName, voiceDevice);
            if (!handler) {
                const errorMessage = `AddVoiceEventListener: Attempting to listen to an unknown event: '${
                    this.#eventName
                }'`;
                return Promise.reject(
                    createFlexSdkError(context)(
                        ErrorCode.UnknownVoiceEventName,
                        { severity: ErrorSeverity.Error },
                        errorMessage
                    )
                );
            }

            voiceDevice.on(this.#eventName, handler);

            return {
                unsubscribe: () => {
                    voiceDevice.off(this.#eventName, handler);
                    logger.debug(`AddVoiceEventListener: Unsubscribed from '${this.#eventName}'`);
                }
            };
        } catch (error) {
            const errorMessage = `AddVoiceEventListener: Failed to add listener for event '${this.#eventName}'`;
            logger.error(errorMessage, error);
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToAddVoiceEventListener,
                    { severity: ErrorSeverity.Error },
                    errorMessage,
                    error
                )
            );
        }
    }

    #createEventHandler(
        context: ContextManager,
        eventName: T,
        voiceDevice: Device
    ): ((...args: unknown[]) => void) | undefined {
        switch (eventName) {
            case VoiceClientEvent.Incoming:
                return (call: Call) => {
                    const voiceCall = new VoiceCallImpl(context, call, voiceDevice);
                    (this.#listener as AddVoiceListenerEvent[VoiceClientEvent.Incoming])(voiceCall);
                };
            case VoiceClientEvent.Error:
                return (twilioError: Error, call?: Call) => {
                    const voiceCall = call ? new VoiceCallImpl(context, call, voiceDevice) : undefined;
                    (this.#listener as AddVoiceListenerEvent[VoiceClientEvent.Error])(twilioError, voiceCall);
                };
            case VoiceClientEvent.Destroyed:
            case VoiceClientEvent.TokenWillExpire:
            case VoiceClientEvent.Unregistered:
                return (...args: unknown[]) => {
                    (this.#listener as (...args: unknown[]) => void)(...args);
                };
            default:
                return undefined;
        }
    }
}
